import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { socketFedRefetchInterval, socketFedStaleTime } from '@/features/websockets/utils/stream-health';
import { executionService } from '../services/execution.service';
import type { ExecutionOverview, ExecutionConfig, ExecutionAnalytics, ExecutionHistoryResponse, ExecutionHistoryFilters, Platform, ExecutionMethod } from '../types/execution.types';

// Query Keys
export const executionKeys = {
  all: ['execution'] as const,
  overview: () => [...executionKeys.all, 'overview'] as const,
  config: () => [...executionKeys.all, 'config'] as const,
  platforms: () => [...executionKeys.all, 'platforms'] as const,
  analytics: (period: string) => [...executionKeys.all, 'analytics', period] as const,
  history: (filters?: ExecutionHistoryFilters) => [...executionKeys.all, 'history', filters] as const,
  screenshots: (executionId: string) => [...executionKeys.all, 'screenshots', executionId] as const
};

// Execution Overview Hook
// Following Pattern 3 from frontend-websocket skill: WebSocket updates React Query cache
export function useExecutionOverview(options) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = executionKeys.overview();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.enabled !== false;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1,
      staleTime: socketFedStaleTime,
      refetchInterval: socketFedRefetchInterval,
      gcTime: 1800000
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}

// Execution Configuration Hook
function _temp() {
  return executionService.getOverview();
}
export function useExecutionConfig() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: executionKeys.config(),
      queryFn: _temp2,
      staleTime: 60000,
      gcTime: 600000
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}

// Update Execution Configuration Mutation
function _temp2() {
  return executionService.getCurrentConfig();
}
export function useUpdateExecutionConfig() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: updatedConfig => {
        queryClient.setQueryData(executionKeys.config(), updatedConfig);
        queryClient.invalidateQueries({
          queryKey: executionKeys.overview()
        });
      },
      onError: _temp4
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Platforms Hook
function _temp4(error) {
  console.error("Failed to update execution config:", error);
}
function _temp3(config) {
  return executionService.updateConfig(config);
}
export function useExecutionPlatforms() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: executionKeys.platforms(),
      queryFn: _temp5,
      staleTime: 120000,
      gcTime: 600000
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}

// Test Platform Mutation
function _temp5() {
  return executionService.getPlatforms();
}
export function useTestPlatform() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: executionKeys.platforms()
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Execution Analytics Hook
// Following Pattern 3 from frontend-websocket skill: WebSocket updates React Query cache
function _temp6(platformId) {
  return executionService.testPlatform(platformId);
}
export function useExecutionAnalytics(t0, options) {
  const $ = _c(8);
  const period = t0 === undefined ? "24h" : t0;
  let t1;
  if ($[0] !== period) {
    t1 = executionKeys.analytics(period);
    $[0] = period;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== period) {
    t2 = () => executionService.getAnalyticsSummary(period);
    $[2] = period;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = options?.enabled !== false;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 300000,
      gcTime: 1800000
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}

// Execution History Hook
export function useExecutionHistory(filters, options) {
  const $ = _c(8);
  let t0;
  if ($[0] !== filters) {
    t0 = executionKeys.history(filters);
    $[0] = filters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== filters) {
    t1 = () => executionService.getExecutionHistory(filters);
    $[2] = filters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = options?.enabled !== false;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 60000,
      gcTime: 600000
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}

// Execution Screenshots Hook
export function useExecutionScreenshots(executionId, options) {
  const $ = _c(8);
  let t0;
  if ($[0] !== executionId) {
    t0 = executionKeys.screenshots(executionId);
    $[0] = executionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== executionId) {
    t1 = () => executionService.getExecutionScreenshots(executionId);
    $[2] = executionId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = options?.enabled !== false && !!executionId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 600000,
      gcTime: 1800000
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}

// Queue Status Hook
// Following Pattern 3 from frontend-websocket skill: WebSocket updates React Query cache
export function useQueueStatus(options) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [...executionKeys.all, "queue"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.enabled !== false;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp7,
      enabled: t1,
      staleTime: socketFedStaleTime,
      refetchInterval: socketFedRefetchInterval
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}

// Available Methods Hook
function _temp7() {
  return executionService.getQueueStatus();
}
export function useAvailableMethods() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [...executionKeys.all, "methods"],
      queryFn: _temp8,
      staleTime: 300000,
      gcTime: 600000
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}

// Method Comparison Hook
function _temp8() {
  return executionService.getAvailableMethods();
}
export function useMethodComparison(t0, options) {
  const $ = _c(7);
  const period = t0 === undefined ? "7d" : t0;
  let t1;
  let t2;
  if ($[0] !== period) {
    t1 = [...executionKeys.all, "comparison", period];
    t2 = () => executionService.getMethodComparison(period);
    $[0] = period;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = options?.enabled !== false;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 120000,
      gcTime: 600000
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
}

// Slippage Analysis Hook
export function useSlippageAnalysis(options) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [...executionKeys.all, "slippage"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.enabled !== false;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp9,
      enabled: t1,
      staleTime: 300000,
      gcTime: 900000
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}

// Utility Hooks
function _temp9() {
  return executionService.getSlippageAnalysis();
}
export function useExecutionMethods() {
  const methods: Array<{
    id: ExecutionMethod;
    name: string;
    description: string;
  }> = [{
    id: 'api',
    name: 'API Execution',
    description: 'Direct API integration with broker platforms'
  }, {
    id: 'browser',
    name: 'Browser Automation',
    description: 'Visual automation through E2B browser sandboxes'
  }, {
    id: 'hybrid',
    name: 'Hybrid Mode',
    description: 'Combination of API and browser automation'
  }, {
    id: 'auto',
    name: 'Automatic Selection',
    description: 'Automatic method selection based on conditions'
  }];
  return methods;
}

// Invalidation Helpers
export function useInvalidateExecutionData() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      invalidateAll: () => {
        queryClient.invalidateQueries({
          queryKey: executionKeys.all
        });
      },
      invalidateOverview: () => {
        queryClient.invalidateQueries({
          queryKey: executionKeys.overview()
        });
      },
      invalidateConfig: () => {
        queryClient.invalidateQueries({
          queryKey: executionKeys.config()
        });
      },
      invalidateAnalytics: period => {
        if (period) {
          queryClient.invalidateQueries({
            queryKey: executionKeys.analytics(period)
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [...executionKeys.all, "analytics"]
          });
        }
      },
      invalidateHistory: () => {
        queryClient.invalidateQueries({
          queryKey: [...executionKeys.all, "history"]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}